import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MenuItem, Typography, useMediaQuery } from '@mui/material';

import { getTheme } from 'app/components/ThemeProvider/theme';
import { useGetTransferDetails, useSendTransferOtp } from 'app/utils/services/transactionsService';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';
import { getAdocUrlFromOctetStream } from 'app/utils/file/adoc';
import { useGetHomePageService } from '../../utils/services/homePageService';
import { enqueueSnackbar } from 'notistack';
import { downloadFileWithAnchorTag } from '../ExtractModal/utils';
import { renderTemplate } from 'app/utils/render';
import { html2pdf } from 'app/utils/html2pdf';

interface Props {
  menuButtons: any;
}
const useLastOperationsTable = (props: Props) => {
  const { menuButtons } = props;

  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeAnchor, setActiveAnchor] = useState('');
  const [id, setId] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const navigate = useCustomNavigate();

  const theme = getTheme('light');
  const mediumScreens = useMediaQuery(theme.breakpoints.down('md'));
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [asanConfirmation, setAsanConfirmation] = useState(false);
  const [snackbar, setSnackbar] = useState(false);

  const { signType } = useOutletContext<{ signType: any }>();

  const [details, setDetails] = useState([]);

  const handleToggleConfirmation = () => {
    setConfirmationDialog(!confirmationDialog);
  };

  const {
    isSuccessGetTransferDetails,
    isLoadingGetTransferDetails,
    getTransferDetailsMutation,
    dataGetTransferDetails,
    transferDetailsMutationVariables,
  } = useGetTransferDetails();

  const { sendTransferOtpMutation, dataSendTransferOtp } = useSendTransferOtp();

  const [otpConfirmation, setOtpConfirmation] = useState(false);

  const handleTransferDetailsResponse = async (transferResponse: any) => {
    if (transferResponse.print_info) {
      const renderedHTML = await renderTemplate('customs-committee.txt', {
        ...transferResponse.print_info,
      });
      await html2pdf(renderedHTML);

      return;
    }

    switch (transferResponse.form_id) {
      case 'details_data':
        {
          setOpenDetailsModal(true);
          setAnchorEl(null);
        }
        break;
      case 'asan_wait_popup':
        {
          setAsanConfirmation(true);
          setAnchorEl(null);
        }
        break;
      case 'otp':
        {
          setOtpConfirmation(true);
        }
        break;
      case 'refresh_page':
        {
          /*
        TODO : use client side operation instead of reloading window
        *  // refetchGetHomePage();
        *  // setConfirmationDialog(false)
        * */

          window?.location.reload();
        }
        break;
      case 'repayment':
        {
          navigate(`transfer-page?type=${transferResponse?.content_data.trn_type}`, {
            state: { templateData: transferResponse?.content_data.data, formId: transferResponse?.form_id },
          });
        }
        break;
      case 'download':
        {
          try {
            const url = await getAdocUrlFromOctetStream(transferResponse.url);

            if (typeof url === 'string') {
              const link: HTMLAnchorElement = document.createElement('a');
              document.body.appendChild(link);
              link.href = url;
              link.download = `${transferResponse.business_key}.adoc`;
              link.click();

              // after the successfully downloading remove element from body
              document.body.removeChild(link);
            } else {
              enqueueSnackbar("Url isn't correct!", {
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'right',
                },
                variant: 'error',
              });
            }
          } catch (error) {
            enqueueSnackbar('Something went wrong when generating file url!', {
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
              variant: 'error',
            });
          }
        }
        break;
      case 'show_file':
        {
          transferResponse?.print_forms &&
            transferResponse.print_forms.forEach((form: { fileLink: string; base_url: string }) => {
              downloadFileWithAnchorTag(`${transferResponse?.base_url}${form.fileLink}`);
            });
        }
        break;
      default:
        setSnackbar(true);
    }
  };

  const handleClickMenu = (value: any, confirmation?: boolean, additionalDetail?: any) => {
    if (value === 'delete' && !confirmation) {
      handleToggleConfirmation();

      return;
    }

    const requestBody: Record<string, string> = {
      action: value,
      trn_id: id,
    };

    if (additionalDetail?.db_cr) {
      requestBody['db_cr'] = additionalDetail?.db_cr;
    }

    getTransferDetailsMutation(requestBody, {
      onSuccess: handleTransferDetailsResponse,
      onError: (error) => {
        setSnackbar(true);
      },
    });
  };

  const handleMenuClick = (event: any, trnId: any, state: any, dbCr: any, trnButtons: string[]) => {
    const filteredMenuButtons: any = Object.fromEntries(
      Object.entries(menuButtons)
        .filter(([key]) => trnButtons.includes(key))
        .map((item: any) => {
          if (item[1].action === 'details') {
            item[1]['db_cr'] = dbCr;
          }

          return item;
        })
    );

    console.log('filteredMenuButtons : ', Object.entries(menuButtons));

    setId(trnId);
    setDetails(filteredMenuButtons);
    setStatus(state);
    setType(dbCr);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setStatus('');
  };

  const handleMoveToTransfer = () => {
    navigate(`transfer-page`);
  };

  useEffect(() => {
    if (anchorEl !== null) {
      setActiveAnchor(anchorEl.getAttribute('aria-controls'));
    }
  }, [anchorEl]);

  return {
    anchorEl,
    setAnchorEl,
    setActiveAnchor,
    handleMenuClick,
    handleClose,
    handleMoveToTransfer,
    activeAnchor,
    mediumScreens,
    details,
    handleClickMenu,
    openDetailsModal,
    setOpenDetailsModal,
    dataGetTransferDetails,
    asanConfirmation,
    setAsanConfirmation,
    status,
    isSuccessGetTransferDetails,
    isLoadingGetTransferDetails,
    signType,
    type,
    snackbar,
    otpConfirmation,
    setOtpConfirmation,
    getTransferDetailsMutation,
    transferDetailsMutationVariables,
    sendTransferOtpMutation,
    dataSendTransferOtp,
    handleToggleConfirmation,
    confirmationDialog,
  };
};

export default useLastOperationsTable;
